import { createConsultationPaymentRequestClient } from '@/api/consultations/createConsultationPaymentRequestClient';
import { QueryKey } from '@/data/query';
import { toast } from '@montugroup/design-system';
import type { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeHeaders, requestHeaders } from '../../api';

export const useCreateConsultationPaymentRequest = (consultationId?: number) => {
  const queryClient = useQueryClient();
  const tsRestClient = createConsultationPaymentRequestClient();
  const headers = normalizeHeaders(requestHeaders());

  const mutationKey = [QueryKey.CreateConsultationPaymentRequest, consultationId];

  const { isPending, error, data, mutateAsync, isSuccess } = tsRestClient.requestPaymentContract.useMutation({
    mutationKey,
    onSuccess: (response) => {
      if (consultationId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.GetPatientConsultations]
        });
      }
      toast.success(response.body.data.message);
    },
    onError: (error) => {
      let errorMessage = 'Failed to create payment request';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'body' in error) {
        errorMessage =
          (error.body as { error?: { message?: string } })?.error?.message || 'Failed to create payment request';
      }

      toast.error(errorMessage);
    }
  });

  const createPaymentRequest = (paymentRequestType: PaymentRequestType) => {
    if (!consultationId) {
      toast.error({
        title: 'Error',
        message: 'Consultation ID is required to create a payment request'
      });
      return Promise.resolve();
    }

    return mutateAsync({
      headers: {
        ...headers,
        authorization: headers['authorization']
      },
      body: {
        consultationId,
        paymentRequestType
      }
    });
  };

  return {
    isPending,
    error,
    data,
    isSuccess,
    createPaymentRequest
  };
};
